import { useId, useState } from 'react'
import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { Frame, FrameCard } from '@/ui/frame'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { CounterKindPicker } from '../../lib/print-settings/counter-kind-picker'
import { usePrinterCounterKinds } from '../../lib/print-settings/printer-counter-kinds-queries'
import { usePrinterFormats } from '../../lib/print-settings/printer-formats-queries'
import { DEFAULT_FORMAT_ORDER, formatLabel } from '../../lib/print-settings/print-settings'
import type { PreferredCounters } from '../../api/preferred-counter-policy-api'

export function PreferredCounterFields({ entityId, value, onChange, disabled, formatsReadOnly = false }: {
  entityId: string
  value: PreferredCounters
  onChange: (next: PreferredCounters) => void
  disabled: boolean
  formatsReadOnly?: boolean
}) {
  const id = useId()
  const formats = usePrinterFormats(entityId)
  const counters = usePrinterCounterKinds(entityId)
  const [dragged, setDragged] = useState<string | null>(null)
  const [dropTarget, setDropTarget] = useState<{ format: string; after: boolean } | null>(null)
  const announce = useAnnounce()
  const formatsEditable = !formatsReadOnly && value.formatPriority.length === DEFAULT_FORMAT_ORDER.length
    && DEFAULT_FORMAT_ORDER.every((format) => value.formatPriority.includes(format))
  let formatHelp = 'The first available format supplies all selected counters. Drag to reorder, or focus a handle and use the arrow keys.'
  if (!formatsEditable) formatHelp = 'This policy’s saved formats differ from the standard Life, Engine, and Equivalent list. Formats and their order are read-only here; other policy fields remain editable.'
  if (formatsReadOnly) formatHelp = 'The latest saved format list is unsupported and read-only here. Your format draft is retained below; review the latest list before saving. Other policy fields remain editable.'

  function move(format: string, destination: number) {
    const source = value.formatPriority.indexOf(format)
    if (disabled || !formatsEditable || source < 0 || source === destination || destination < 0 || destination >= value.formatPriority.length) return
    const next = value.formatPriority.filter((item) => item !== format)
    next.splice(destination, 0, format)
    onChange({ ...value, formatPriority: next })
    announce(`${formatLabel(format, formats.data ?? [])} is now ${destination + 1} of ${next.length}`)
  }

  function endDrag() {
    setDragged(null)
    setDropTarget(null)
  }

  return (
    <Frame title="Preferred counters" description="Copied into each printer’s settings once, when it first appears. Later policy changes do not overwrite its settings.">
      <FrameCard className="flex flex-col gap-6 p-4">
        <section className="flex flex-col gap-2" aria-labelledby={`${id}-priority`}>
          <h3 id={`${id}-priority`} className="text-sm font-medium">Format priority</h3>
          <p id={`${id}-priority-help`} className="text-muted-foreground text-xs">
            {formatHelp}
          </p>
          {formats.isLoading && <LoadingNotice message="Loading format labels…" />}
          {formats.isError && <RetryNotice message="Couldn’t load format labels." onRetry={() => void formats.refetch()} />}
          <ol aria-labelledby={`${id}-priority`} className="divide-border border-border divide-y rounded-md border">
            {value.formatPriority.map((format, index) => {
              const label = formatLabel(format, formats.data ?? [])
              return (
                <li key={format} className={cn('relative flex items-center gap-2 px-3 py-2', dragged === format && 'opacity-50')}
                  onDragOver={(event) => {
                    if (disabled || !formatsEditable || !dragged || dragged === format) return
                    event.preventDefault()
                    const bounds = event.currentTarget.getBoundingClientRect()
                    setDropTarget({ format, after: event.clientY >= bounds.top + bounds.height / 2 })
                  }}
                  onDragLeave={(event) => {
                    if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setDropTarget(null)
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (dragged && dropTarget?.format === format) {
                      const source = value.formatPriority.indexOf(dragged)
                      const insertion = index + (dropTarget.after ? 1 : 0)
                      move(dragged, insertion - (source < insertion ? 1 : 0))
                    }
                    endDrag()
                  }}>
                  {dropTarget?.format === format && !disabled && (
                    <span aria-hidden className={cn('bg-foreground pointer-events-none absolute inset-x-0 z-10 h-0.5', dropTarget.after ? 'bottom-0' : 'top-0')} />
                  )}
                  {formatsEditable && (
                    <>
                      <button type="button" draggable={!disabled} disabled={disabled}
                        aria-label={`Reorder ${label}`} aria-describedby={`${id}-priority-help`}
                        className="text-muted-foreground cursor-grab rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'move'
                          event.dataTransfer.setData('text/plain', format)
                          setDragged(format)
                        }} onDragEnd={endDrag}
                        onKeyDown={(event) => {
                          if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
                          event.preventDefault()
                          move(format, index + (event.key === 'ArrowUp' ? -1 : 1))
                        }}>
                        <GripVertical aria-hidden className="size-4" />
                      </button>
                      <div className="flex flex-col">
                        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Move ${label} up`} disabled={disabled || index === 0} onClick={() => move(format, index - 1)}><ChevronUp aria-hidden /></Button>
                        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Move ${label} down`} disabled={disabled || index === value.formatPriority.length - 1} onClick={() => move(format, index + 1)}><ChevronDown aria-hidden /></Button>
                      </div>
                    </>
                  )}
                  <span className="text-muted-foreground w-4 text-xs tabular-nums">{index + 1}</span>
                  <span className="flex-1 text-sm">{label}</span>
                </li>
              )
            })}
          </ol>
        </section>
        <section className="flex flex-col gap-2 [&_fieldset]:grid [&_fieldset]:grid-cols-2 [&_fieldset]:gap-x-4" aria-labelledby={`${id}-counters`}>
          <span id={`${id}-counters`} className="sr-only">Counter selection</span>
          <CounterKindPicker value={value.countersToDisplay} options={(counters.data ?? []).map((counter) => ({
            ...counter,
            group: counter.group === 'core' ? 'core counters' : counter.group === 'other' ? 'other counters' : counter.group,
          }))}
            labelledBy={`${id}-counters`}
            describedBy={value.countersToDisplay.length === 0 ? `${id}-counter-error` : undefined}
            disabled={disabled}
            optionsState={counters.isLoading ? 'loading' : counters.isError ? 'error' : 'ready'}
            onRetry={() => void counters.refetch()}
            onChange={(countersToDisplay) => onChange({ ...value, countersToDisplay })} />
          {value.countersToDisplay.length === 0 && (
            <p id={`${id}-counter-error`} role="alert" className="text-destructive text-xs">
              Choose at least one counter.
            </p>
          )}
        </section>
      </FrameCard>
    </Frame>
  )
}
